import { useFeatureFlags } from '@commander/feature-flags';
import type { User } from '@commander/utils';
import { appRegistry, crossAppState, toNavSectionPath } from '@commander/utils';

// App ID union type matching the backend app_access definition
type AppId =
  | 'crm'
  | 'prospecting'
  | 'success'
  | 'routing'
  | 'driver'
  | 'haulercentral'
  | 'global-search';

import {
  RiApps2Line as Apps2Icon,
  RiBuildingLine as Building2Icon,
  RiArrowDownSLine as ChevronDownIcon,
  RiCommandLine as CommandIcon,
  RiDashboardLine as DashboardIcon,
  RiSearchLine as SearchIcon,
  RiStore2Line as Store2Icon,
  RiTrophyLine as SuccessIcon,
  RiTruckLine as TruckIcon,
  RiUserSearchLine as UserSearchIcon,
} from '@remixicon/react';
import type React from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

// Icon mapping for dynamic icons
const iconMap: Record<string, React.ComponentType<any>> = {
  Building2Icon,
  UserSearchIcon,
  CommandIcon,
  SuccessIcon,
  Apps2Icon,
  DashboardIcon,
  Store2Icon,
  TruckIcon,
  SearchIcon,
};

interface AppSwitcherMenuProps {
  appName?: string;
  appColor?: string;
  user?: User | null;
  collapsed?: boolean;
}

export function AppSwitcherMenu({ appName, appColor, user, collapsed }: AppSwitcherMenuProps) {
  const { flags } = useFeatureFlags();
  const location = useLocation();
  // Helper function to check if user has access to a specific app
  // Undefined app_access is intentionally treated as no access to mirror backend policy
  const hasAppAccess = (appId: AppId): boolean => {
    // Flag-gated apps: the feature flag grants access (not restricted by app_access)
    // DEPRECATED (2026-10-15): 'haulercentral' is not in appRegistry.apps, so this check never
    // runs; HaulerCentral is reached via flag-gated shell routes. See docs/cleanup-backlog.md.
    if (appId === 'haulercentral') {
      return flags?.haulercentral ?? false;
    }
    const flag = appRegistry.getAppById(appId)?.featureFlag;
    if (flag) {
      // Registry flag keys are plain strings, so read through the widened map getUserApps takes.
      const flagMap: Record<string, boolean> | undefined = flags;
      return flagMap?.[flag] ?? false;
    }
    if (!user?.company_employee?.app_access) {
      return false; // If no access restrictions defined, deny all apps
    }
    // Type assertion needed because User type may not include all new apps yet
    return (user.company_employee.app_access as string[]).includes(appId);
  };

  const apps = appRegistry
    .getUserApps(user?.company_employee?.app_access as string[] | undefined, flags)
    .filter((app) => app.id !== 'driver' || flags?.routing_driver_app);

  // Get current app from registry or use provided props
  let currentApp = appRegistry.getCurrentApp();

  // If no app detected from registry, use provided props
  if (!currentApp && appName) {
    currentApp = {
      id: 'current',
      name: appName,
      icon: 'Building2Icon',
      color: appColor || '#059669',
      description: '',
      path: window.location.pathname,
      port: Number.parseInt(window.location.port, 10) || 3000,
      status: 'active' as const,
    };
  }

  // Fallback to first available app
  if (!currentApp) {
    currentApp = apps[0];
  }

  useEffect(() => {
    if (!currentApp) return;
    // Keyed on location, not just currentApp: getCurrentApp() returns a stable
    // reference, so keying on it alone only ever recorded the landing route.
    // We store the left-nav section so returning users get their tab back
    // without resurrecting a stale deep link.
    const section = toNavSectionPath(location.pathname);
    if (section) {
      crossAppState.saveLastRoute(currentApp.id, section);
    }
  }, [currentApp, location.pathname]);

  const handleNavigate = (targetAppId: string) => {
    const targetApp = appRegistry.getAppById(targetAppId);

    if (!targetApp || targetApp.status !== 'active') {
      return;
    }

    // Save current state before navigation
    crossAppState.saveNavigationContext(currentApp?.id || 'unknown', targetAppId, {
      fromPath: window.location.pathname,
    });

    // Get the URL for the target app - this should include the correct port
    const lastRoute = crossAppState.getLastRoute(targetAppId);
    const targetUrl = appRegistry.getAppUrl(
      targetAppId,
      lastRoute ? lastRoute.replace(targetApp.path, '') : ''
    );

    window.location.href = targetUrl;
  };

  // Get the icon component for current app
  const CurrentAppIcon = iconMap[currentApp?.icon || 'Building2Icon'] || Building2Icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {collapsed ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-lg text-sidebar-accent-foreground hover:bg-sidebar-accent"
          >
            <div
              className="h-8 w-8 rounded-[10px] flex items-center justify-center shrink-0"
              style={{
                backgroundColor:
                  currentApp?.name === 'Home' ? `${currentApp?.color}15` : currentApp?.color,
              }}
            >
              <CurrentAppIcon
                className="h-8 w-8"
                style={{ color: currentApp?.name === 'Home' ? currentApp?.color : '#FFF' }}
              />
            </div>
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="lg"
            className="gap-2 p-2 rounded-lg text-sidebar-accent-foreground flex items-center justify-between w-full hover:bg-sidebar-accent"
          >
            <div className="flex items-center gap-2">
              <div
                className="h-8 w-8 rounded-[10px] flex items-center justify-center shrink-0"
                style={{
                  backgroundColor:
                    currentApp?.name === 'Home' ? `${currentApp?.color}15` : currentApp?.color,
                }}
              >
                <CurrentAppIcon
                  className="h-8 w-8"
                  style={{ color: currentApp?.name === 'Home' ? currentApp?.color : '#FFF' }}
                />
              </div>
              <span className="text-sm font-semibold truncate">{currentApp?.name}</span>
            </div>
            <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 fill-sidebar-accent-foreground" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[240px] p-1">
        <DropdownMenuLabel className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
          Applications
        </DropdownMenuLabel>

        {/* Active Apps */}
        {apps
          .filter((app) => app.status === 'active')
          .filter((app) => app.id !== 'success' || hasAppAccess('success'))
          .map((app) => {
            const Icon = iconMap[app.icon] || Building2Icon;
            const isActive = currentApp?.id === app.id;
            const hasAccess = hasAppAccess(app.id as AppId);

            return (
              <DropdownMenuItem
                key={app.id}
                onClick={() => hasAccess && handleNavigate(app.id)}
                disabled={!hasAccess}
                className={`gap-2 px-2 py-2 rounded-md focus:ring-offset-1 focus:ring-offset-sidebar-ring ${isActive ? 'bg-accent' : ''} ${!hasAccess ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div
                  className="h-8 w-8 rounded-md flex items-center justify-center shrink-0"
                  style={{ backgroundColor: app?.color }}
                >
                  <Icon className="h-4 w-4" style={{ color: '#FFF' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{app.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{app.description}</div>
                </div>
                {!hasAccess ? (
                  <span className="text-[10px] font-medium text-destructive shrink-0">
                    Contact Sales
                  </span>
                ) : null}
              </DropdownMenuItem>
            );
          })}

        {/* Coming Soon Apps */}
        {apps.filter((app) => app.status === 'coming-soon').length > 0 && (
          <>
            <DropdownMenuSeparator className="my-1" />
            <DropdownMenuLabel className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
              Coming Soon
            </DropdownMenuLabel>
            {apps
              .filter((app) => app.status === 'coming-soon')
              .map((app) => {
                const Icon = iconMap[app.icon] || Building2Icon;

                return (
                  <DropdownMenuItem
                    key={app.id}
                    disabled
                    className="gap-2 px-2 py-2 rounded-md opacity-60"
                  >
                    <div
                      className="h-8 w-8 rounded-md flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${app.color}15` }}
                    >
                      <Icon className="h-4 w-4" style={{ color: app.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{app.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {app.description}
                      </div>
                    </div>
                    <span className="text-[10px] font-medium text-muted-foreground shrink-0">
                      Soon
                    </span>
                  </DropdownMenuItem>
                );
              })}
          </>
        )}

        <DropdownMenuSeparator className="my-1" />
        <DropdownMenuItem
          onClick={() => {
            // Navigate to Platform Dashboard (Home)
            window.location.href = appRegistry.getShellUrl('/');
          }}
          className="gap-2 px-2 py-2 rounded-md"
        >
          <div className="h-8 w-8 rounded-md flex items-center justify-center shrink-0 bg-muted">
            <DashboardIcon className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium">Platform Dashboard</div>
            <div className="text-xs text-muted-foreground">Overview and settings</div>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
